"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@promptwatch/ui/components/accordion";
import Link from "next/link";

export default function FAQSection() {
  const faqItems = [
    {
      id: "item-1",
      question: "What is Promptwatch and how does it work?",
      answer:
        "Promptwatch is the leading Generative Engine Optimization (GEO) and AI visibility platform. We monitor, analyze, and optimize your brand's presence in AI-generated search results and conversations across platforms like ChatGPT, Claude, Gemini, Perplexity, and Google AI Overviews.",
    },
    {
      id: "item-2",
      question: "What is Generative Engine Optimization (GEO)?",
      answer:
        "GEO is the practice of optimizing your content and brand presence for AI-powered search engines and conversational AI platforms. As users increasingly turn to AI for answers, GEO ensures your brand appears prominently and accurately in AI-generated responses.",
    },
    {
      id: "item-3",
      question: "Which AI platforms does Promptwatch monitor?",
      answer:
        "We monitor all major AI platforms including ChatGPT, Claude, Gemini, Perplexity, Grok, Google AI Overviews, Bing AI, You.com, and many others. Our monitoring capabilities are continuously expanding to cover new AI platforms as they emerge.",
    },
    {
      id: "item-4",
      question: "How can Promptwatch help improve my brand's AI visibility?",
      answer:
        "Promptwatch provides real-time monitoring of brand mentions, identifies content gaps, analyzes prompt performance, and offers optimization recommendations. We help you understand how AI systems represent your brand and provide actionable insights to improve your visibility.",
    },
    {
      id: "item-5",
      question: "Can I track my competitors' AI visibility?",
      answer:
        "Yes! Promptwatch offers competitive AI benchmarking that allows you to analyze competitor visibility and strategies within AI platforms. See how your brand compares and identify opportunities to gain a competitive advantage in AI search results.",
    },
    {
      id: "item-6",
      question: "Does Promptwatch support multiple languages?",
      answer:
        "Absolutely! Promptwatch supports localization and can monitor AI conversations and brand mentions across multiple languages, helping you understand your global brand presence in AI platforms.",
    },
    {
      id: "item-7",
      question: "Who should use Promptwatch?",
      answer:
        "Promptwatch is ideal for CMOs, marketing directors, brand managers, digital agencies, and enterprises in competitive sectors like SaaS, tech, retail, finance, travel, and media who want to maintain control over their brand representation in AI search results.",
    },
    {
      id: "item-8",
      question: "How do I get started with Promptwatch?",
      answer:
        "Getting started is easy! Book a demo to see how Promptwatch can help optimize your brand's AI visibility. Our team will show you how to monitor your brand across AI platforms and implement effective GEO strategies for your business.",
    },
  ];

  // Generate structured data for FAQ
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqStructuredData),
        }}
      />
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-5 lg:gap-16">
        {/* Title Section - Left */}
        <div className="flex flex-col lg:col-span-2">
          <h3 className="text-title text-4xl font-medium max-w-2xl text-left sm:text-5xl lg:text-3xl">Frequently Asked Questions</h3>
          <p className="text-muted-foreground mt-4 text-md">
            Learn about AI visibility monitoring and how Promptwatch helps your brand succeed in AI search. Still have questions?{" "}
            <Link href="/book-a-demo" className="text-primary font-medium hover:underline">
              Book a demo
            </Link>
          </p>
        </div>

        {/* FAQ Section - Right */}
        <div className="lg:col-span-3">
          <Accordion type="single" collapsible className="bg-card ring-muted w-full rounded-2xl border px-8 py-3 shadow-sm ring-4">
            {faqItems.map((item) => (
              <AccordionItem key={item.id} value={item.id} className="border-dashed">
                <AccordionTrigger className="cursor-pointer text-base hover:no-underline">{item.question}</AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm text-muted-foreground ">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </>
  );
}
